 import { useState, useRef, useCallback } from "react";
import "./AIPicker.css";
import React from 'react'
import axios from 'axios'

export const backEndUri = process.env.REACT_APP_TREEOSK_BACKEND_URI ? process.env.REACT_APP_TREEOSK_BACKEND_URI : 'http://localhost:4000';

const IMAGE_SIZES = [
    { value: "1024x1024", label: "1024×1024 (Carré)" },
    { value: "1024x1792", label: "1024×1792 (Portrait)" },
    { value: "1792x1024", label: "1792×1024 (Paysage)" },
];

const IMAGE_QUALITIES = [
    { value: "standard", label: "Standard" },
    { value: "hd", label: "HD" },
];

const IMAGE_STYLES = [
    { value: "vivid", label: "Vivid" },
    { value: "natural", label: "Natural" },
];

const COMPONENT_STYLES = [
    { value: "modern", label: "Moderne" },
    { value: "minimal", label: "Minimal" },
    { value: "classic", label: "Classique" },
    { value: "playful", label: "Ludique" },
];

const TYPE_CONFIG = {
    text: { icon: "✍️", label: "Texte", title: "Générer du texte" },
    image: { icon: "🖼️", label: "Image", title: "Générer une image" },
    imageRef: { icon: "🎨", label: "Image", title: "Générer avec référence" },
    component: { icon: "🧩", label: "Composant", title: "Générer un composant" },
};

/**
 * AIPicker - Composant pour générer du contenu avec l'IA
 * 
 * @param {string} type - Type de génération: "text" | "image" | "imageRef" | "component"
 * @param {function} onResult - Callback appelé avec le résultat { type, data }
 * @param {string} buttonLabel - Label du bouton (optionnel)
 * @param {string} buttonIcon - Icône du bouton (optionnel)
 */
export default function AIPicker({ 
    type = "text", 
    onResult,
    buttonLabel,
    buttonIcon = "🤖"
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const [prompt, setPrompt] = useState("");
    const [imageSize, setImageSize] = useState("1024x1024");
    const [imageQuality, setImageQuality] = useState("standard");
    const [imageStyle, setImageStyle] = useState("vivid");
    const [referenceImage, setReferenceImage] = useState(null);
    const [referencePreview, setReferencePreview] = useState(null);
    const [componentStyle, setComponentStyle] = useState("modern");
    const [useStreaming, setUseStreaming] = useState(true);

    const [codeTab, setCodeTab] = useState("html");

    const fileInputRef = useRef(null);

    const config = TYPE_CONFIG[type] || TYPE_CONFIG.text;

    const resetForm = () => {
        setPrompt("");
        setReferenceImage(null);
        setReferencePreview(null);
        setResult(null);
        setError(null);
    };

    const handleClose = () => {
        setIsOpen(false);
        //resetForm();
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            setReferenceImage(event.target.result);
            setReferencePreview(event.target.result);
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!prompt.trim()) return;

        setIsLoading(true);
        setError(null);
        setResult(null);

        try {

            switch (type) {
                case "component":
                    await axios.post(`${backEndUri}/ai/component`, {description: prompt, style: componentStyle}).then((e) => {
                        setResult({type: "component", data: e.data.data})
                        console.log(e)
                    })
                    // response = { success: true, data: { html, css, js } }
                    // setResult({ type: "component", data: response.data });
                    break;

                default:
                    break;
            }
        } catch (err) {
            console.error("AI Error:", err);
            const errMsg = (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || "Une erreur est survenue";
            setError(errMsg);
        } finally {
            setIsLoading(false);
            setIsStreaming(false);
        }
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
    };

    /**
     * Génère le HTML complet avec CSS et JS intégrés pour un composant
     */
    const getFullComponentHtml = (componentData) => {
        const html = (componentData && componentData.html) || '';
        const css = (componentData && componentData.css) || '';
        const js = (componentData && componentData.js) || '';
        
        let fullHtml = '';

        // Ajouter le CSS en style inline
        if (css.trim()) {
            fullHtml += `<style>${css}</style>\n`;
        }

        // Ajouter le HTML
        if (html.trim()) {
            fullHtml += html.trim();
        }

        // Ajouter le JS en script inline
        if (js.trim()) {
            fullHtml += `\n<script>${js}</script>`;
        }

        console.log('Generated full HTML:', { html, css, js, fullHtml });
        return fullHtml;
    };

    const handleUseResult = () => {
        if (result && onResult) {
            // Pour les composants, on envoie le HTML complet avec CSS/JS intégrés
            if (result.type === "component" && result.data) {
                const fullHtml = getFullComponentHtml(result.data);
                onResult({
                    type: "component",
                    data: fullHtml,
                    raw: result.data // Garder les données brutes si besoin
                });
            } else {
                onResult(result);
            }
            handleClose();
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    const getPreviewHtml = useCallback(() => {
        if (!result || result.type !== "component") return "";
        const { html, css, js } = result.data;
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <style>${css}</style>
            </head>
            <body>
                ${html}
                <script>${js}</script>
            </body>
            </html>
        `;
    }, [result]);

    const renderForm = () => {
        switch (type) {
            case "text":
                return (
                    <>
                        <div className="ai-picker-field">
                            <label className="ai-picker-label">Votre prompt</label>
                            <textarea
                                className="ai-picker-textarea"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="Décrivez le texte que vous souhaitez générer..."
                            />
                        </div>
                        <div className="ai-picker-field">
                            <label className="ai-picker-label ai-picker-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={useStreaming}
                                    onChange={(e) => setUseStreaming(e.target.checked)}
                                />
                                Génération en temps réel (streaming)
                            </label>
                        </div>
                    </>
                );

            case "image":
                return (
                    <>
                        <div className="ai-picker-field">
                            <label className="ai-picker-label">Description de l'image</label>
                            <textarea
                                className="ai-picker-textarea"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="Décrivez l'image que vous souhaitez générer..."
                            />
                        </div>
                        <div className="ai-picker-options-row">
                            <div className="ai-picker-field">
                                <label className="ai-picker-label">Taille</label>
                                <select
                                    className="ai-picker-select"
                                    value={imageSize}
                                    onChange={(e) => setImageSize(e.target.value)}
                                >
                                    {IMAGE_SIZES.map((s) => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="ai-picker-field">
                                <label className="ai-picker-label">Qualité</label>
                                <select
                                    className="ai-picker-select"
                                    value={imageQuality}
                                    onChange={(e) => setImageQuality(e.target.value)}
                                >
                                    {IMAGE_QUALITIES.map((q) => (
                                        <option key={q.value} value={q.value}>{q.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="ai-picker-field">
                                <label className="ai-picker-label">Style</label>
                                <select
                                    className="ai-picker-select"
                                    value={imageStyle}
                                    onChange={(e) => setImageStyle(e.target.value)}
                                >
                                    {IMAGE_STYLES.map((st) => (
                                        <option key={st.value} value={st.value}>{st.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </>
                );

            case "imageRef":
                return (
                    <>
                        <div className="ai-picker-field">
                            <label className="ai-picker-label">Image de référence</label>
                            <div
                                className={`ai-picker-image-upload ${referencePreview ? "has-image" : ""}`}
                                onClick={() => { if (fileInputRef && fileInputRef.current && fileInputRef.current.click) fileInputRef.current.click(); }}
                            >
                                {referencePreview ? (
                                    <img
                                        src={referencePreview}
                                        alt="Référence"
                                        className="ai-picker-preview-image"
                                    />
                                ) : (
                                    <>
                                        <span className="ai-picker-upload-icon">📤</span>
                                        <span className="ai-picker-upload-text">
                                            Cliquez pour sélectionner une image
                                        </span>
                                    </>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="ai-picker-upload-input"
                                    onChange={handleImageUpload}
                                />
                            </div>
                        </div>
                        <div className="ai-picker-field">
                            <label className="ai-picker-label">Instructions</label>
                            <textarea
                                className="ai-picker-textarea"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="Décrivez les modifications ou le style souhaité..."
                            />
                        </div>
                        <div className="ai-picker-options-row">
                            <div className="ai-picker-field">
                                <label className="ai-picker-label">Taille</label>
                                <select
                                    className="ai-picker-select"
                                    value={imageSize}
                                    onChange={(e) => setImageSize(e.target.value)}
                                >
                                    {IMAGE_SIZES.map((s) => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </>
                );

            case "component":
                return (
                    <>
                        <div className="ai-picker-field">
                            <label className="ai-picker-label">Description du composant</label>
                            <textarea
                                className="ai-picker-textarea"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="Décrivez le composant que vous souhaitez créer (ex: un bouton animé avec effet de vague)..."
                            />
                        </div>
                        <div className="ai-picker-options-row">
                            <div className="ai-picker-field">
                                <label className="ai-picker-label">Style visuel</label>
                                <select
                                    className="ai-picker-select"
                                    value={componentStyle}
                                    onChange={(e) => setComponentStyle(e.target.value)}
                                >
                                    {COMPONENT_STYLES.map((s) => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </>
                );

            default:
                return null;
        }
    };

    const renderResult = () => {
        if (!result) return null;

        switch (result.type) {
            case "text":
                return (
                    <div className="ai-picker-result">
                        <div className="ai-picker-result-header">
                            <span className="ai-picker-result-title">
                                <span>✅</span> Texte généré
                            </span>
                            <div className="ai-picker-result-actions">
                                <button
                                    type="button"
                                    className="ai-picker-action-btn"
                                    onClick={() => handleCopy(result.data)}
                                >
                                    📋 Copier
                                </button>
                                <button
                                    type="button"
                                    className="ai-picker-action-btn primary"
                                    onClick={handleUseResult}
                                >
                                    ✓ Utiliser
                                </button>
                            </div>
                        </div>
                        <div className="ai-picker-text-result">
                            {result.data}
                            {isStreaming && <span className="ai-picker-streaming-cursor" />}
                        </div>
                    </div>
                );

            case "image":
                return (
                    <div className="ai-picker-result">
                        <div className="ai-picker-result-header">
                            <span className="ai-picker-result-title">
                                <span>✅</span> Image générée
                            </span>
                            <div className="ai-picker-result-actions">
                                <button
                                    type="button"
                                    className="ai-picker-action-btn primary"
                                    onClick={handleUseResult}
                                >
                                    ✓ Utiliser
                                </button>
                            </div>
                        </div>
                        <div className="ai-picker-image-result">
                            {result.data.map((url, index) => (
                                <img
                                    key={index}
                                    src={url}
                                    alt={`Generated ${index + 1}`}
                                    className="ai-picker-generated-image"
                                />
                            ))}
                        </div>
                    </div>
                );

            case "component":
                return (
                    <div className="ai-picker-result">
                        <div className="ai-picker-result-header">
                            <span className="ai-picker-result-title">
                                <span>✅</span> Composant généré
                            </span>
                            <div className="ai-picker-result-actions">
                                <button
                                    type="button"
                                    className="ai-picker-action-btn primary"
                                    onClick={handleUseResult}
                                >
                                    ✓ Utiliser
                                </button>
                            </div>
                        </div>
                        <div className="ai-picker-component-result">
                            <div className="ai-picker-code-tabs">
                                <button
                                    type="button"
                                    className={`ai-picker-code-tab ${codeTab === "html" ? "active" : ""}`}
                                    onClick={() => setCodeTab("html")}
                                >
                                    HTML
                                </button>
                                <button
                                    type="button"
                                    className={`ai-picker-code-tab ${codeTab === "css" ? "active" : ""}`}
                                    onClick={() => setCodeTab("css")}
                                >
                                    CSS
                                </button>
                                <button
                                    type="button"
                                    className={`ai-picker-code-tab ${codeTab === "js" ? "active" : ""}`}
                                    onClick={() => setCodeTab("js")}
                                >
                                    JavaScript
                                </button>
                                <button
                                    type="button"
                                    className={`ai-picker-code-tab ${codeTab === "preview" ? "active" : ""}`}
                                    onClick={() => setCodeTab("preview")}
                                >
                                    Aperçu
                                </button>
                            </div>
                            {codeTab !== "preview" ? (
                                <div className="ai-picker-code-block">
                                    <button
                                        type="button"
                                        className="ai-picker-copy-btn"
                                        onClick={() => handleCopy(result.data[codeTab])}
                                    >
                                        📋 Copier
                                    </button>
                                    <pre>{result.data[codeTab] || "(vide)"}</pre>
                                </div>
                            ) : (
                                <div className="ai-picker-preview">
                                    <iframe
                                        srcDoc={getPreviewHtml()}
                                        title="Component Preview"
                                        sandbox="allow-scripts"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="ai-picker-container">
            {/* Bouton pour ouvrir le modal */}
            <button 
                type="button"
                className="ai-picker-trigger"
                onClick={() => setIsOpen(true)}
            >
                <span className="ai-picker-trigger-icon">{buttonIcon}</span>
                <span className="ai-picker-trigger-label">
                    {buttonLabel || config.title}
                </span>
            </button>

            {/* Modal */}
            {isOpen && (
                <div className="ai-picker-overlay" onClick={handleOverlayClick}>
                    <div className="ai-picker-modal" onClick={(e) => e.stopPropagation()}>
                        {/* Header */}
                        <div className="ai-picker-header">
                            <h2 className="ai-picker-title">
                                <span className="ai-picker-title-icon">{config.icon}</span>
                                {config.title}
                            </h2>
                            <button 
                                type="button" 
                                className="ai-picker-close" 
                                onClick={handleClose}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Content */}
                        <div className="ai-picker-content">
                            {isLoading && !isStreaming ? (
                                <div className="ai-picker-loading">
                                    <div className="ai-picker-spinner" />
                                    <span className="ai-picker-loading-text">
                                        Génération en cours...
                                    </span>
                                </div>
                            ) : (
                                <>
                                    {error && (
                                        <div className="ai-picker-error">
                                            <span className="ai-picker-error-icon">⚠️</span>
                                            {error}
                                        </div>
                                    )}

                                    {result ? (
                                        <>
                                            {renderResult()}
                                            <button
                                                type="button"
                                                className="ai-picker-submit ai-picker-submit-secondary"
                                                onClick={resetForm}
                                            >
                                                🔄 Nouvelle génération
                                            </button>
                                        </>
                                    ) : (
                                        <form className="ai-picker-form" onSubmit={handleSubmit}>
                                            {renderForm()}
                                            <button
                                                type="submit"
                                                className="ai-picker-submit"
                                                disabled={!prompt.trim() || isLoading}
                                            >
                                                <span className="ai-picker-submit-icon">✨</span>
                                                Générer
                                            </button>
                                        </form>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
